/**
 * Field Mapping Utilities for Entity CRUD Forms
 * 
 * CRITICAL DESIGN PATTERN (January 2026):
 * =========================================
 * All HTML form field IDs MUST follow the pattern: {TableName}_{TableFieldName}
 * 
 * This allows:
 * 1. Automatic population from API response by enumerating FieldDescriptions
 * 2. Automatic collection for save by enumerating FieldDescriptions
 * 3. No manual mapping layer - field ID IS the API field reference
 * 4. Self-documenting code - developers know exactly which API field is referenced
 * 
 * SELF-CONTAINED COMPONENT PATTERN:
 * =================================
 * Forms should not need to know about specific field types. The fieldMapping utilities
 * automatically detect FieldTypeName and create appropriate components:
 * - Date → DatePicker (if {id}-container exists)
 * - CurrencyAmountPeriod → CurrencyAmountPeriod component
 * 
 * Container naming: {TableName}_{TableFieldName}-container
 * Example: ContactConfigFields_AvailabilityDate-container → DatePicker created here
 * 
 * @see specifications/TriSys-Web-API-Integration.md - Field Naming Convention section
 */

import { Logger } from '@/services/Logger';
import { trisysAPI, FieldDescriptionValue, StandingDataFieldDescription } from '@/services/api/TriSysWebAPI';
import { trisysApi } from '../services/TriSysApiService';
import { DatePicker } from '@/components/DatePicker';
import { CurrencyAmountPeriod, CurrencyAmountPeriodValue } from '@/components/CurrencyAmountPeriod';
import { CurrencyAmount, CurrencyAmountValue } from '@/components/CurrencyAmount';
import { FileLink } from '@/components/FileLink';
import { ContactLink } from '@/components/ContactLink';
import { CompanyLink } from '@/components/CompanyLink';
import { TelephoneService } from '@/services/TelephoneService';

const logger = Logger.scope('FieldMapping');

/**
 * Field value structure from ReadEntity API
 */
export interface FieldValue {
  value: string | number | boolean | null;
  displayValue: string | null;
}

/**
 * FieldDescription from Standing Data
 * Re-export the StandingDataFieldDescription type for convenience
 */
export type FieldDescription = StandingDataFieldDescription;

/**
 * Component registry for auto-created components
 * Used to track DatePickers, CurrencyAmountPeriod, CurrencyAmount, etc. for collection and cleanup
 */
export interface ComponentRegistry {
  datePickers: Map<string, DatePicker>;  // htmlId -> DatePicker
  currencyAmountPeriods: Map<string, CurrencyAmountPeriod>;  // htmlId -> CurrencyAmountPeriod
  currencyAmounts: Map<string, CurrencyAmount>;  // htmlId -> CurrencyAmount
  fileLinks: Map<string, FileLink>;  // htmlId -> FileLink (for File Reference fields)
  contactLinks: Map<string, ContactLink>;  // htmlId -> ContactLink (for Contact fields)
  companyLinks: Map<string, CompanyLink>;  // htmlId -> CompanyLink (for Company fields)
}

// ============================================================================
// FIELD TRANSFORMATIONS
// ============================================================================
// Some fields violate standard FieldDescription patterns and need special handling.
// Rather than scatter these exceptions across forms, we centralize them here.
//
// Examples:
// - ContactMarried: boolean in API → "Single"/"Married" dropdown in UI
// - Boolean fields: true/false in API → "Yes"/"No" dropdown or checkbox
// ============================================================================

/**
 * Field transformation for populate (API → UI)
 */
export interface FieldTransformPopulate {
  /** HTML field ID this transformation applies to */
  htmlId: string;
  /** Transform API value to UI value */
  transform: (apiValue: FieldValue) => string | boolean | number | null;
}

/**
 * Field transformation for collect (UI → API)  
 */
export interface FieldTransformCollect {
  /** HTML field ID this transformation applies to */
  htmlId: string;
  /** Transform UI value to API value */
  transform: (uiValue: string) => string | boolean | number | null;
}

/**
 * Entity field transformations registry
 * Maps entityType to field transformations
 */
interface EntityFieldTransformations {
  populate: FieldTransformPopulate[];
  collect: FieldTransformCollect[];
}

/**
 * Registry of field transformations by entity type
 * Add entity-specific exceptions here
 */
const fieldTransformations: Record<string, EntityFieldTransformations> = {
  Contact: {
    populate: [
      {
        // ContactMarried is a BOOLEAN in API but we use a dropdown (0=Single, 1=Married)
        htmlId: 'Contact_ContactMarried',
        transform: (apiValue: FieldValue) => {
          const raw = apiValue.value;
          // API returns true/false boolean - convert to "0" or "1" for select value
          if (raw === true || raw === 'true' || raw === 'True' || raw === '1') return '1'; // Married
          if (raw === false || raw === 'false' || raw === 'False' || raw === '0') return '0'; // Single
          return '';
        }
      }
    ],
    collect: [
      {
        // Convert dropdown value (0/1) back to boolean for API
        htmlId: 'Contact_ContactMarried',
        transform: (uiValue: string) => {
          return uiValue === '1' ? 'true' : 'false';
        }
      }
    ]
  }
  // Add other entities here as needed:
  // Company: { populate: [...], collect: [...] },
  // Requirement: { populate: [...], collect: [...] },
};

/**
 * Apply field transformations after auto-populate
 * Call this after autoPopulateFormFields() to handle special fields
 * 
 * @param entityType - Entity type (e.g., "Contact")
 * @param fieldData - Map of HTML field ID -> FieldValue (same as passed to autoPopulateFormFields)
 */
export function applyFieldTransformations(
  entityType: string,
  fieldData: Map<string, FieldValue>
): void {
  const transformations = fieldTransformations[entityType];
  if (!transformations) return;
  
  for (const t of transformations.populate) {
    const fieldValue = fieldData.get(t.htmlId);
    if (!fieldValue) continue;
    
    const transformedValue = t.transform(fieldValue);
    if (transformedValue === null || transformedValue === undefined) continue;
    
    // Find the element and set the transformed value
    const element = document.getElementById(t.htmlId);
    if (!element) {
      logger.debug('Transform target element not found', { htmlId: t.htmlId });
      continue;
    }
    
    if (element instanceof HTMLSelectElement) {
      element.value = String(transformedValue);
    } else if (element instanceof HTMLInputElement) {
      if (element.type === 'checkbox') {
        element.checked = !!transformedValue;
      } else {
        element.value = String(transformedValue);
      }
    } else if (element instanceof HTMLTextAreaElement) {
      element.value = String(transformedValue);
    }
    
    logger.debug('Applied field transformation', { 
      htmlId: t.htmlId, 
      originalValue: fieldValue.value,
      transformedValue 
    });
  }
}

/**
 * Get the collect transformation for a field (used by autoCollectFormData)
 */
export function getCollectTransformation(
  entityType: string, 
  htmlId: string
): ((uiValue: string) => string | boolean | number | null) | null {
  const transformations = fieldTransformations[entityType];
  if (!transformations) return null;
  
  const t = transformations.collect.find(t => t.htmlId === htmlId);
  return t?.transform || null;
}

// Global component registry - one per form context
// Forms should call createComponentRegistry() and pass it to populate/collect functions
let _globalRegistry: ComponentRegistry | null = null;

/**
 * Create a new component registry for a form
 */
export function createComponentRegistry(): ComponentRegistry {
  return {
    datePickers: new Map(),
    currencyAmountPeriods: new Map(),
    currencyAmounts: new Map(),
    fileLinks: new Map(),
    contactLinks: new Map(),
    companyLinks: new Map()
  };
}

/**
 * Get or create the global component registry
 */
function getGlobalRegistry(): ComponentRegistry {
  if (!_globalRegistry) {
    _globalRegistry = createComponentRegistry();
  }
  return _globalRegistry;
}

/**
 * Destroy all components in a registry and clear it
 */
export function destroyComponentRegistry(registry: ComponentRegistry): void {
  for (const [_, picker] of registry.datePickers) {
    picker.destroy();
  }
  registry.datePickers.clear();
  
  for (const [_, cap] of registry.currencyAmountPeriods) {
    cap.destroy();
  }
  registry.currencyAmountPeriods.clear();
  
  for (const [_, ca] of registry.currencyAmounts) {
    ca.destroy();
  }
  registry.currencyAmounts.clear();
  
  for (const [_, fl] of registry.fileLinks) {
    fl.destroy();
  }
  registry.fileLinks.clear();
  
  for (const [_, cl] of registry.contactLinks) {
    cl.destroy();
  }
  registry.contactLinks.clear();
  
  for (const [_, comp] of registry.companyLinks) {
    comp.destroy();
  }
  registry.companyLinks.clear();
  
  logger.debug('Destroyed component registry');
}

/**
 * Build HTML field ID from FieldDescription
 * Pattern: {TableName}_{TableFieldName}
 */
export function buildFieldId(fd: FieldDescription): string {
  return `${fd.TableName}_${fd.TableFieldName}`;
}

/**
 * Build HTML field ID from table name and field name
 * Pattern: {TableName}_{TableFieldName}
 */
export function makeFieldId(tableName: string, tableFieldName: string): string {
  return `${tableName}_${tableFieldName}`;
}

/**
 * Parse an HTML field ID to extract table name and field name
 * @param fieldId - HTML field ID in format "TableName_FieldName"
 * @returns { tableName, fieldName } or null if invalid format
 */
export function parseFieldId(fieldId: string): { tableName: string; fieldName: string } | null {
  const underscoreIndex = fieldId.indexOf('_');
  if (underscoreIndex === -1) return null;
  
  return {
    tableName: fieldId.substring(0, underscoreIndex),
    fieldName: fieldId.substring(underscoreIndex + 1)
  };
}

/**
 * AUTO-POPULATE LOOKUP OPTIONS FROM DOM
 * =====================================
 * This is the CORRECT pattern: enumerate the DOM to find all select elements,
 * look up their FieldDescription, and populate options from Skills by FieldId.
 * 
 * NO HARDCODING of field names or FieldIds - the HTML declares which fields exist.
 * 
 * @param container - Container element to search within (defaults to document)
 */
export function autoPopulateLookupOptions(container: HTMLElement | Document = document): void {
  console.log('🔵 autoPopulateLookupOptions: START');
  try {
    const standingData = trisysAPI.getCachedStandingData();
    if (!standingData?.FieldDescriptions || !standingData?.Skills) {
      logger.warn('Standing data not loaded, cannot populate lookup options');
      return;
    }
    
    console.log('🔵 autoPopulateLookupOptions: Building fdByKey map');
    // Build FieldDescription lookup by TableName + TableFieldName
    const fdByKey = new Map<string, FieldDescription>();
    for (const fd of standingData.FieldDescriptions) {
      const key = `${fd.TableName}_${fd.TableFieldName}`;
      fdByKey.set(key, fd);
    }
    
    console.log('🔵 autoPopulateLookupOptions: Finding selects');
    // Find all select elements in the container
    const selects = container.querySelectorAll('select[id]');
    console.log('🔵 autoPopulateLookupOptions: Found', selects.length, 'selects');
    let populatedCount = 0;
  
  for (const select of selects) {
    const htmlId = select.id;
    
    // Skip if doesn't match TableName_FieldName pattern
    const parsed = parseFieldId(htmlId);
    if (!parsed) continue;
    
    // Look up FieldDescription
    const fd = fdByKey.get(htmlId);
    if (!fd) continue;
    
    // Get Skills that populate this field (by FieldId)
    const fieldSkills = standingData.Skills
      .filter(s => s.FieldId === fd.FieldId)
      .sort((a, b) => a.Ordering - b.Ordering);
    
    // If no skills for this FieldId, skip (not a skill-based lookup)
    if (fieldSkills.length === 0) continue;
    
    // Populate the select with options
    const selectEl = select as HTMLSelectElement;
    
    // Preserve existing options if already populated
    if (selectEl.options.length > 1) continue;
    
    // Clear and add placeholder
    selectEl.innerHTML = '';
    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = 'Select...';
    selectEl.appendChild(placeholder);
    
    // Add skill options (value = skill name, for API compatibility)
    for (const skill of fieldSkills) {
      const option = document.createElement('option');
      option.value = skill.Skill;  // API expects skill name text, not SkillId
      option.textContent = skill.Skill;
      selectEl.appendChild(option);
    }
    
    populatedCount++;
    logger.debug('Populated lookup options', { 
      htmlId, 
      fieldId: fd.FieldId, 
      optionCount: fieldSkills.length 
    });
  }
  
  console.log('🔵 autoPopulateLookupOptions: DONE');
  logger.debug('Auto-populated lookup options from DOM', { 
    selectsFound: selects.length, 
    populatedCount 
  });
  } catch (error) {
    console.error('🔴 autoPopulateLookupOptions ERROR:', error);
    throw error;
  }
}

/**
 * Get FieldDescriptions for an entity type from cached Standing Data
 * Returns fields for both the main entity table and its ConfigFields table
 * 
 * @param entityType - Entity type (e.g., "Contact", "Company", "Requirement")
 * @returns Array of FieldDescriptions
 */
export function getEntityFieldDescriptions(entityType: string): FieldDescription[] {
  const standingData = trisysAPI.getCachedStandingData();
  if (!standingData?.FieldDescriptions) {
    logger.warn('No FieldDescriptions in cached Standing Data');
    return [];
  }
  
  // Entity tables follow patterns: "Contact" and "ContactConfigFields"
  const tableNames = [entityType, `${entityType}ConfigFields`];
  
  return standingData.FieldDescriptions.filter(
    (fd: FieldDescription) => tableNames.includes(fd.TableName)
  );
}

/**
 * Convert API FieldDescriptionValues to a map keyed by HTML field ID
 * 
 * @param fieldDescriptionValues - Array of { FieldId, Value, DisplayValue } from ReadEntity
 * @returns Map of { [htmlFieldId]: FieldValue }
 */
export function fieldValuesToHtmlIdMap(
  fieldDescriptionValues: Array<{ FieldId: number; Value: unknown; DisplayValue: string | null }>
): Map<string, FieldValue> {
  const standingData = trisysAPI.getCachedStandingData();
  if (!standingData?.FieldDescriptions) {
    logger.warn('No FieldDescriptions in cached Standing Data');
    return new Map();
  }
  
  // Build FieldId -> FieldDescription lookup
  const fdByFieldId = new Map<number, FieldDescription>();
  for (const fd of standingData.FieldDescriptions) {
    fdByFieldId.set(fd.FieldId, fd);
  }
  
  // Convert to HTML ID keyed map
  const result = new Map<string, FieldValue>();
  for (const fv of fieldDescriptionValues) {
    const fd = fdByFieldId.get(fv.FieldId);
    if (fd) {
      const htmlId = buildFieldId(fd);
      result.set(htmlId, {
        value: fv.Value as string | number | boolean | null,
        displayValue: fv.DisplayValue
      });
    }
  }
  
  logger.debug('Converted field values to HTML ID map', { 
    inputCount: fieldDescriptionValues.length,
    outputCount: result.size 
  });
  
  return result;
}

/**
 * Auto-populate form fields from API data by enumerating FieldDescriptions.
 * 
 * This is the core of the "scientific" approach: iterate through all known fields
 * for an entity and populate any matching HTML elements.
 * 
 * SELF-CONTAINED: Automatically creates DatePicker and CurrencyAmountPeriod components
 * when it finds a container element (e.g., {htmlId}-container).
 * 
 * @param entityType - Entity type (e.g., "Contact")
 * @param fieldData - Map of HTML field ID -> FieldValue
 * @param options - Optional configuration
 */
export async function autoPopulateFormFields(
  entityType: string,
  fieldData: Map<string, FieldValue>,
  options: {
    skipFields?: string[];  // Field IDs to skip
    dateFormat?: Intl.DateTimeFormatOptions;
    container?: HTMLElement;  // Container to search within (defaults to document)
    registry?: ComponentRegistry;  // Component registry for cleanup
  } = {}
): Promise<{ populated: string[]; skipped: string[]; notFound: string[]; components: string[] }> {
  const result = { populated: [] as string[], skipped: [] as string[], notFound: [] as string[], components: [] as string[] };
  const container = options.container || document;
  const skipFields = new Set(options.skipFields || []);
  const registry = options.registry || getGlobalRegistry();
  
  // Defensive check: ensure fieldData is a valid Map
  if (!fieldData || !(fieldData instanceof Map)) {
    logger.error('autoPopulateFormFields: fieldData is not a Map', { 
      fieldData, 
      type: typeof fieldData,
      isNull: fieldData === null,
      isUndefined: fieldData === undefined 
    });
    return result;
  }
  
  const fieldDescriptions = getEntityFieldDescriptions(entityType);
  
  // Defensive check: ensure fieldDescriptions is an array
  if (!Array.isArray(fieldDescriptions)) {
    logger.error('autoPopulateFormFields: fieldDescriptions is not an array', { 
      fieldDescriptions, 
      type: typeof fieldDescriptions 
    });
    return result;
  }
  
  const standingData = trisysAPI.getCachedStandingData();
  
  for (const fd of fieldDescriptions) {
    let htmlId = '(unknown)';
    try {
      htmlId = buildFieldId(fd);
      
      if (skipFields.has(htmlId)) {
        result.skipped.push(htmlId);
        continue;
      }
      
      const fieldValue = fieldData.get(htmlId);
      
      // Check for component container first (e.g., {htmlId}-container)
      const componentContainer = container.querySelector(`#${CSS.escape(htmlId)}-container`) as HTMLElement;
    
    // Handle Date fields with DatePicker
    if (fd.FieldTypeName === 'Date' && componentContainer) {
      if (!registry.datePickers.has(htmlId)) {
        const picker = new DatePicker(componentContainer, {
          name: htmlId,
          placeholder: 'Select date...',
          size: 'sm',
          showTodayButton: true
        });
        await picker.initialize();
        registry.datePickers.set(htmlId, picker);
        result.components.push(htmlId);
      }
      
      // Set value
      if (fieldValue?.value) {
        try {
          const valueStr = typeof fieldValue.value === 'string' 
            ? fieldValue.value 
            : String(fieldValue.value);
          const date = new Date(valueStr);
          if (!isNaN(date.getTime())) {
            registry.datePickers.get(htmlId)?.setValue(date);
          }
        } catch (err) {
          logger.warn('Failed to set DatePicker value', { htmlId, value: fieldValue.value, error: err });
        }
      }
      
      result.populated.push(htmlId);
      continue;
    }
    
    // Handle CurrencyAmountPeriod fields
    if (fd.FieldTypeName === 'CurrencyAmountPeriod' && componentContainer) {
      if (!registry.currencyAmountPeriods.has(htmlId) && standingData) {
        const currencies = (standingData.Currencies || []).map(c => ({
          CurrencyId: c.CurrencyId,
          Symbol: c.Symbol,
          Description: c.Description,
          Ordering: c.Ordering
        }));
        
        const periods = (standingData.ConsultancyPeriods || []).map(p => ({
          ConsultancyPeriodId: p.ConsultancyPeriodId,
          Name: p.Name
        }));
        
        if (currencies.length > 0 && periods.length > 0) {
          const defaultCurrencyId = currencies.find(c => c.Symbol === '£')?.CurrencyId || currencies[0]?.CurrencyId || 1;
          const defaultPeriodId = periods.find(p => p.Name === 'Annum')?.ConsultancyPeriodId || periods[0]?.ConsultancyPeriodId || 5;
          
          const cap = new CurrencyAmountPeriod(componentContainer, {
            name: htmlId,
            currencies,
            periods,
            currencyId: defaultCurrencyId,
            amount: 0,
            periodId: defaultPeriodId,
            size: 'sm'
          });
          await cap.initialize();
          registry.currencyAmountPeriods.set(htmlId, cap);
          result.components.push(htmlId);
        }
      }
      
      // Set value from DisplayValue (NOT Value - which is a database record ID!)
      // See specifications/TriSys-Web-API-Integration.md "CurrencyAmountPeriod (FieldTypeId 39) - Special Handling"
      const displayValue = fieldValue?.displayValue;
      if (displayValue && registry.currencyAmountPeriods.has(htmlId)) {
        try {
          // DisplayValue is the formatted string like "£50,000.00 per Annum"
          logger.debug('CurrencyAmountPeriod displayValue received', { 
            htmlId, 
            displayValue,
            rawValue: fieldValue.value  // This is database ID, not amount!
          });
          const valueStr = typeof displayValue === 'string' 
            ? displayValue 
            : String(displayValue);
          if (valueStr && valueStr.trim()) {
            logger.debug('Setting CurrencyAmountPeriod from displayValue', { htmlId, valueStr });
            registry.currencyAmountPeriods.get(htmlId)?.setValueFromString(valueStr);
          }
        } catch (err) {
          logger.warn('Failed to set CurrencyAmountPeriod value', { htmlId, displayValue, error: err });
        }
      }
      
      result.populated.push(htmlId);
      continue;
    }
    
    // Handle File Reference fields with FileLink
    if (fd.FieldTypeName === 'File Reference' && registry.fileLinks.has(htmlId)) {
      const fileLink = registry.fileLinks.get(htmlId);
      if (fileLink && fieldValue?.value) {
        try {
          // Value is the file path (e.g., "C:\Files\document.pdf")
          const filePath = typeof fieldValue.value === 'string' 
            ? fieldValue.value 
            : String(fieldValue.value);
          if (filePath.trim()) {
            fileLink.setFilePath(filePath);
            logger.debug('FileLink populated', { htmlId, filePath });
          }
        } catch (err) {
          logger.warn('Failed to set FileLink value', { htmlId, value: fieldValue.value, error: err });
        }
      }
      result.populated.push(htmlId);
      continue;
    }
    
    // Handle Contact fields with ContactLink
    if (fd.FieldTypeName === 'Contact' && registry.contactLinks.has(htmlId)) {
      const contactLink = registry.contactLinks.get(htmlId);
      if (contactLink && fieldValue?.value) {
        try {
          // Value is the ContactId, displayValue is the contact name
          const contactId = typeof fieldValue.value === 'number' 
            ? fieldValue.value 
            : parseInt(String(fieldValue.value), 10);
          const displayName = fieldValue.displayValue || null;
          if (!isNaN(contactId) && contactId > 0) {
            contactLink.setContact(contactId, displayName);
            logger.debug('ContactLink populated', { htmlId, contactId, displayName });
          }
        } catch (err) {
          logger.warn('Failed to set ContactLink value', { htmlId, value: fieldValue.value, error: err });
        }
      }
      result.populated.push(htmlId);
      continue;
    }
    
    // Handle Company fields with CompanyLink
    if (fd.FieldTypeName === 'Company' && registry.companyLinks.has(htmlId)) {
      const companyLink = registry.companyLinks.get(htmlId);
      if (companyLink && fieldValue?.value) {
        try {
          // Value is the CompanyId, displayValue is the company name
          const companyId = typeof fieldValue.value === 'number' 
            ? fieldValue.value 
            : parseInt(String(fieldValue.value), 10);
          const displayName = fieldValue.displayValue || null;
          if (!isNaN(companyId) && companyId > 0) {
            companyLink.setCompany(companyId, displayName);
            logger.debug('CompanyLink populated', { htmlId, companyId, displayName });
          }
        } catch (err) {
          logger.warn('Failed to set CompanyLink value', { htmlId, value: fieldValue.value, error: err });
        }
      }
      result.populated.push(htmlId);
      continue;
    }
    
    // Handle Image fields - set src on img element
    if (fd.FieldTypeName === 'Image') {
      const imgElement = container.querySelector(`#${CSS.escape(htmlId)}`) as HTMLImageElement;
      const placeholder = container.querySelector(`#${CSS.escape(htmlId)}-placeholder`) as HTMLElement;
      
      if (imgElement) {
        if (fieldValue?.value) {
          try {
            // Value is typically a local path (G:\...) or URL
            let imageSrc = typeof fieldValue.value === 'string' 
              ? fieldValue.value 
              : String(fieldValue.value);
            
            // Convert local paths to API URL if it's a Windows path
            if (imageSrc && (imageSrc.includes(':\\') || imageSrc.startsWith('\\\\'))) {
              // Use GetFileURL API to fetch a web-accessible URL
              const fileUrlResult = await trisysAPI.getFileUrl(imageSrc);
              if (fileUrlResult.Success && fileUrlResult.URL) {
                imageSrc = fileUrlResult.URL;
                imgElement.setAttribute('data-file-path', String(fieldValue.value)); // Store original path
                logger.debug('Image path converted to URL', { htmlId, originalPath: fieldValue.value, url: imageSrc });
              } else {
                logger.warn('Failed to get file URL for image', { htmlId, path: imageSrc, error: fileUrlResult.ErrorMessage });
                imageSrc = ''; // Clear invalid path
              }
            }
            
            if (imageSrc && imageSrc.trim()) {
              // Just set src - the onload/onerror handlers in renderField will handle visibility
              imgElement.src = imageSrc;
              logger.debug('Image src set, waiting for onload', { htmlId, src: imageSrc });
            } else {
              // No valid URL - clear image and show placeholder
              imgElement.src = '';
              imgElement.removeAttribute('data-file-path');
              imgElement.classList.add('hidden');
              if (placeholder) placeholder.classList.remove('hidden');
            }
          } catch (err) {
            logger.warn('Failed to set Image value', { htmlId, value: fieldValue.value, error: err });
          }
        } else {
          // No value - clear image and show placeholder
          imgElement.src = '';
          imgElement.removeAttribute('data-file-path');
          imgElement.classList.add('hidden');
          if (placeholder) placeholder.classList.remove('hidden');
          logger.debug('Image cleared (no value)', { htmlId });
        }
      }
      result.populated.push(htmlId);
      continue;
    }
    
    // Standard element handling
    const element = container.querySelector(`#${CSS.escape(htmlId)}`) as HTMLElement;
    if (!element) {
      result.notFound.push(htmlId);
      continue;
    }
    
    if (!fieldValue) {
      continue; // No value from API, leave field empty
    }
    
    // Determine what value to use based on field type
    const displayVal = fieldValue.displayValue;
    const rawVal = fieldValue.value;
    
    // Set value based on element type
    if (element instanceof HTMLInputElement) {
      if (element.type === 'checkbox') {
        element.checked = !!rawVal;
      } else if (element.type === 'date') {
        // Convert to YYYY-MM-DD format for date inputs
        if (rawVal && typeof rawVal === 'string') {
          const date = new Date(rawVal);
          if (!isNaN(date.getTime())) {
            element.value = date.toISOString().split('T')[0];
          }
        }
      } else {
        // For text/email/tel inputs, prefer displayValue for lookups
        element.value = (displayVal || rawVal || '') as string;
      }
    } else if (element instanceof HTMLSelectElement) {
      // For selects, try matching in this order:
      // 1. Match option.value to raw value (e.g., ID match)
      // 2. Match option.value to displayValue (e.g., text match) - only if displayValue is non-empty
      // 3. Match option.text to rawValue or displayValue (case-insensitive)
      const rawValStr = rawVal != null ? String(rawVal) : '';
      const displayValStr = displayVal || '';
      
      let matched = false;
      for (const opt of element.options) {
        // Skip empty/placeholder options when matching
        if (!opt.value && opt.value !== rawValStr) continue;
        
        if (opt.value === rawValStr || 
            (displayValStr && opt.value === displayValStr) ||  // Only check if displayValStr is non-empty
            opt.text === rawValStr ||
            opt.text.toLowerCase() === rawValStr.toLowerCase() ||
            (displayValStr && opt.text === displayValStr) ||
            (displayValStr && opt.text.toLowerCase() === displayValStr.toLowerCase())) {
          element.value = opt.value;
          matched = true;
          break;
        }
      }
      
      if (!matched && (rawValStr || displayValStr)) {
        logger.debug('Select value not matched', { 
          htmlId, 
          rawVal, 
          displayVal,
          options: Array.from(element.options).map(o => ({ value: o.value, text: o.text }))
        });
      }
    } else if (element instanceof HTMLTextAreaElement) {
      element.value = (displayVal || rawVal || '') as string;
    }
    
    result.populated.push(htmlId);
    
    } catch (fieldError) {
      // Catch error processing individual field
      console.error('🔴 ERROR processing field:', htmlId, 'FieldTypeName:', fd?.FieldTypeName, 'Error:', fieldError);
      logger.error('Error processing field in autoPopulateFormFields', { 
        htmlId, 
        fieldId: fd?.FieldId,
        fieldTypeName: fd?.FieldTypeName,
        tableFieldName: fd?.TableFieldName,
        error: fieldError instanceof Error ? fieldError.message : String(fieldError),
        stack: fieldError instanceof Error ? fieldError.stack : undefined
      });
      throw fieldError; // Re-throw to preserve original error
    }
  }
  
  logger.debug('Auto-populated form fields', { 
    entityType,
    populatedCount: result.populated.length,
    skippedCount: result.skipped.length,
    notFoundCount: result.notFound.length
  });
  
  return result;
}

/**
 * Auto-collect form data for API save by enumerating FieldDescriptions.
 * 
 * SELF-CONTAINED: Automatically collects from DatePicker and CurrencyAmountPeriod
 * components if they exist in the registry.
 * 
 * @param entityType - Entity type (e.g., "Contact")
 * @param options - Optional configuration
 * @returns Object with TableFieldName keys and values for API
 */
export function autoCollectFormData(
  entityType: string,
  options: {
    skipFields?: string[];  // HTML field IDs to skip
    container?: HTMLElement;
    registry?: ComponentRegistry;
  } = {}
): Record<string, string | number | boolean | null> {
  const result: Record<string, string | number | boolean | null> = {};
  const container = options.container || document;
  const skipFields = new Set(options.skipFields || []);
  const registry = options.registry || getGlobalRegistry();
  
  const fieldDescriptions = getEntityFieldDescriptions(entityType);
  
  for (const fd of fieldDescriptions) {
    const htmlId = buildFieldId(fd);
    
    if (skipFields.has(htmlId)) {
      continue;
    }
    
    // Check for DatePicker component first
    if (fd.FieldTypeName === 'Date' && registry.datePickers.has(htmlId)) {
      const picker = registry.datePickers.get(htmlId);
      const date = picker?.getValue();
      if (date) {
        // API expects YYYY-MM-DD format, not full ISO string
        result[fd.TableFieldName] = date.toISOString().split('T')[0];
      }
      continue;
    }
    
    // Check for CurrencyAmountPeriod component
    if (fd.FieldTypeName === 'CurrencyAmountPeriod' && registry.currencyAmountPeriods.has(htmlId)) {
      const cap = registry.currencyAmountPeriods.get(htmlId);
      if (cap) {
        const value = cap.getValue();
        if (value && value.amount > 0) {
          // Use getApiValue() for correct API format: "£50000 per Annum"
          result[fd.TableFieldName] = cap.getApiValue();
        }
      }
      continue;
    }
    
    // Check for FileLink component
    if (fd.FieldTypeName === 'File Reference' && registry.fileLinks.has(htmlId)) {
      const fileLink = registry.fileLinks.get(htmlId);
      if (fileLink) {
        const filePath = fileLink.getFilePath();
        if (filePath) {
          result[fd.TableFieldName] = filePath;
        }
      }
      continue;
    }
    
    // Check for ContactLink component
    if (fd.FieldTypeName === 'Contact' && registry.contactLinks.has(htmlId)) {
      const contactLink = registry.contactLinks.get(htmlId);
      if (contactLink) {
        const contactId = contactLink.getContactId();
        if (contactId && contactId > 0) {
          result[fd.TableFieldName] = contactId;
        }
      }
      continue;
    }
    
    // Check for CompanyLink component
    if (fd.FieldTypeName === 'Company' && registry.companyLinks.has(htmlId)) {
      const companyLink = registry.companyLinks.get(htmlId);
      if (companyLink) {
        const companyId = companyLink.getCompanyId();
        if (companyId && companyId > 0) {
          result[fd.TableFieldName] = companyId;
        }
      }
      continue;
    }
    
    // Check for Image field - value stored in data-file-path attribute
    if (fd.FieldTypeName === 'Image') {
      const imgElement = container.querySelector(`#${CSS.escape(htmlId)}`) as HTMLImageElement;
      if (imgElement) {
        const filePath = imgElement.getAttribute('data-file-path');
        if (filePath) {
          result[fd.TableFieldName] = filePath;
        }
      }
      continue;
    }
    
    // Standard element handling
    const element = container.querySelector(`#${CSS.escape(htmlId)}`) as HTMLElement;
    if (!element) {
      continue; // Field not on form - skip
    }
    
    // Collect value based on element type
    let value: string | number | boolean | null = null;
    
    if (element instanceof HTMLInputElement) {
      if (element.type === 'checkbox') {
        value = element.checked;
      } else if (element.type === 'number') {
        value = element.value ? parseFloat(element.value) : null;
      } else {
        // Check for data-file-path attribute (FileLink components store full path here)
        const filePath = element.getAttribute('data-file-path');
        if (filePath) {
          value = filePath;
        } else {
          value = element.value || null;
        }
      }
    } else if (element instanceof HTMLSelectElement) {
      value = element.value || null;
    } else if (element instanceof HTMLTextAreaElement) {
      value = element.value || null;
    }
    
    // Apply collect transformation if defined (e.g., ContactMarried: "Single" → "false")
    if (value !== null && typeof value === 'string') {
      const transform = getCollectTransformation(entityType, htmlId);
      if (transform) {
        const transformed = transform(value);
        value = transformed !== null ? transformed : value;
      }
    }
    
    // Use TableFieldName as the key (not the HTML ID)
    if (value !== null && value !== '') {
      result[fd.TableFieldName] = value;
    }
  }
  
  logger.debug('Auto-collected form data', { 
    entityType,
    fieldCount: Object.keys(result).length
  });
  
  return result;
}

/**
 * Convert collected form data (key-value object) to FieldDescriptionValue[] format
 * required by the UpdateEntity API.
 * 
 * @param entityType - Entity type (e.g., "Contact")
 * @param formData - Object with { TableFieldName: value } pairs
 * @returns Array of FieldDescriptionValue for the API
 */
export function toFieldDescriptionValues(
  entityType: string,
  formData: Record<string, string | number | boolean | null>
): FieldDescriptionValue[] {
  const fieldDescriptions = getEntityFieldDescriptions(entityType);
  
  // Build TableFieldName -> FieldDescription lookup (need full FD for FieldTypeName)
  const fdByName = new Map<string, StandingDataFieldDescription>();
  for (const fd of fieldDescriptions) {
    fdByName.set(fd.TableFieldName, fd);
  }
  
  // Get Users from standing data for User field type lookups
  const standingData = trisysAPI.getCachedStandingData();
  const usersById = new Map<number, string>();
  if (standingData?.Users) {
    for (const user of standingData.Users) {
      usersById.set(user.UserId, user.FullName || '');
    }
  }
  
  const result: FieldDescriptionValue[] = [];
  
  for (const [fieldName, value] of Object.entries(formData)) {
    const fd = fdByName.get(fieldName);
    if (!fd) {
      logger.warn('No FieldDescription found for field', { fieldName, entityType });
      continue;
    }
    
    let apiValue: string | number | boolean | null = value;
    
    // User fields (FieldTypeId 18): value is UserId, but API expects FullName
    if (fd.FieldTypeName === 'User' && value !== null && value !== '') {
      const userId = typeof value === 'string' ? parseInt(value, 10) : value as number;
      const userName = usersById.get(userId);
      if (userName) {
        apiValue = userName;
        logger.debug('Converted User field from ID to name', { 
          fieldName, userId, userName 
        });
      } else {
        logger.warn('User ID not found in standing data', { fieldName, userId });
      }
    }
    
    result.push({
      FieldId: fd.FieldId,
      Value: apiValue,
      DisplayValue: null  // API will compute DisplayValue for lookups
    });
  }
  
  logger.debug('Converted to FieldDescriptionValues', { 
    entityType, 
    inputFields: Object.keys(formData).length,
    outputFields: result.length 
  });
  
  return result;
}

/**
 * Track original field values for dirty checking.
 * Call this after populating a form to capture the initial state.
 * 
 * @param entityType - Entity type (e.g., "Contact")
 * @param container - Container element
 * @returns Map of HTML field ID -> original value
 */
export function captureOriginalFieldValues(
  entityType: string,
  container: HTMLElement = document.body
): Map<string, string> {
  const originalValues = new Map<string, string>();
  const fieldDescriptions = getEntityFieldDescriptions(entityType);
  
  for (const fd of fieldDescriptions) {
    const htmlId = buildFieldId(fd);
    const element = container.querySelector(`#${CSS.escape(htmlId)}`) as HTMLElement;
    
    if (!element) continue;
    
    let value = '';
    if (element instanceof HTMLInputElement) {
      value = element.type === 'checkbox' ? String(element.checked) : element.value;
    } else if (element instanceof HTMLSelectElement) {
      value = element.value;
    } else if (element instanceof HTMLTextAreaElement) {
      value = element.value;
    }
    
    originalValues.set(htmlId, value);
  }
  
  return originalValues;
}

/**
 * Check if any fields have changed from original values.
 * 
 * @param originalValues - Map of HTML field ID -> original value
 * @param container - Container element
 * @returns { isDirty: boolean, changedFields: string[] }
 */
export function checkFormDirty(
  originalValues: Map<string, string>,
  container: HTMLElement = document.body
): { isDirty: boolean; changedFields: string[] } {
  const changedFields: string[] = [];
  
  for (const [htmlId, originalValue] of originalValues) {
    const element = container.querySelector(`#${CSS.escape(htmlId)}`) as HTMLElement;
    if (!element) continue;
    
    let currentValue = '';
    if (element instanceof HTMLInputElement) {
      currentValue = element.type === 'checkbox' ? String(element.checked) : element.value;
    } else if (element instanceof HTMLSelectElement) {
      currentValue = element.value;
    } else if (element instanceof HTMLTextAreaElement) {
      currentValue = element.value;
    }
    
    if (currentValue !== originalValue) {
      changedFields.push(htmlId);
    }
  }
  
  return {
    isDirty: changedFields.length > 0,
    changedFields
  };
}

// ============================================================================
// FORM VALIDATION
// ============================================================================
// Validate form fields based on FieldDescription.Mandatory flag.
// For lookup fields, validates that the value exists in the lookup options.
// ============================================================================

/**
 * Validation error for a single field
 */
export interface FieldValidationError {
  /** HTML field ID */
  htmlId: string;
  /** FieldId from FieldDescription */
  fieldId: number;
  /** Display label for the field */
  fieldLabel: string;
  /** Human-readable error message */
  message: string;
}

/**
 * Validation result for the entire form
 */
export interface FormValidationResult {
  /** Whether the form is valid */
  isValid: boolean;
  /** List of validation errors */
  errors: FieldValidationError[];
}

/**
 * Validate mandatory fields for an entity form.
 * Checks that all fields marked as Mandatory in FieldDescription have valid values.
 * For Lookup fields, also validates that the value exists in the lookup options.
 * 
 * @param entityType - Entity type (e.g., "Contact")
 * @param options - Optional configuration
 * @returns Validation result with isValid flag and errors array
 */
export function validateMandatoryFields(
  entityType: string,
  options: {
    container?: HTMLElement;
    registry?: ComponentRegistry;
  } = {}
): FormValidationResult {
  const errors: FieldValidationError[] = [];
  const container = options.container || document;
  const registry = options.registry || getGlobalRegistry();
  
  const fieldDescriptions = getEntityFieldDescriptions(entityType);
  
  // Only validate fields marked as Mandatory
  const mandatoryFields = fieldDescriptions.filter(fd => fd.Mandatory);
  
  logger.debug('Validating mandatory fields', { 
    entityType, 
    mandatoryCount: mandatoryFields.length,
    fields: mandatoryFields.map(f => f.TableFieldName)
  });
  
  for (const fd of mandatoryFields) {
    const htmlId = buildFieldId(fd);
    const label = fd.FieldLabel || fd.TableFieldName;
    
    let value: string | number | boolean | null = null;
    let hasValue = false;
    
    // Check for DatePicker component first
    if (fd.FieldTypeName === 'Date' && registry.datePickers.has(htmlId)) {
      const picker = registry.datePickers.get(htmlId);
      const date = picker?.getValue();
      hasValue = date !== null && date !== undefined;
    }
    // Check for CurrencyAmountPeriod component
    else if (fd.FieldTypeName === 'CurrencyAmountPeriod' && registry.currencyAmountPeriods.has(htmlId)) {
      const cap = registry.currencyAmountPeriods.get(htmlId);
      if (cap) {
        const capValue = cap.getValue();
        hasValue = capValue !== null && capValue.amount > 0;
      }
    }
    // Check for FileLink component
    else if (fd.FieldTypeName === 'File Reference' && registry.fileLinks.has(htmlId)) {
      const fileLink = registry.fileLinks.get(htmlId);
      if (fileLink) {
        const filePath = fileLink.getFilePath();
        hasValue = filePath !== null && filePath !== '';
      }
    }
    // Standard element handling
    else {
      const element = container.querySelector(`#${CSS.escape(htmlId)}`) as HTMLElement;
      
      if (!element) {
        // Field not on form - skip validation (might be on a different tab/section)
        continue;
      }
      
      if (element instanceof HTMLInputElement) {
        if (element.type === 'checkbox') {
          // Checkboxes are always "valid" - they have a true/false value
          hasValue = true;
        } else {
          value = element.value;
          hasValue = value !== null && value !== '';
        }
      } else if (element instanceof HTMLSelectElement) {
        value = element.value;
        hasValue = value !== null && value !== '';
        
        // For lookup fields, validate value exists in options
        if (hasValue && fd.Lookup) {
          const validOptions = getFieldLookupOptions(fd);
          const isValidOption = validOptions.some(opt => opt.value === value);
          if (!isValidOption) {
            errors.push({
              htmlId,
              fieldId: fd.FieldId,
              fieldLabel: label,
              message: `${label} must be a valid selection`
            });
            continue;
          }
        }
      } else if (element instanceof HTMLTextAreaElement) {
        value = element.value;
        hasValue = value !== null && value.trim() !== '';
      }
    }
    
    // Check if mandatory field has a value
    if (!hasValue) {
      errors.push({
        htmlId,
        fieldId: fd.FieldId,
        fieldLabel: label,
        message: `${label} is required`
      });
    }
  }
  
  logger.debug('Validation complete', { 
    entityType,
    isValid: errors.length === 0,
    errorCount: errors.length,
    errors: errors.map(e => e.message)
  });
  
  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Highlight validation errors on the form by adding error styling to invalid fields.
 * Also scrolls to and focuses the first invalid field.
 * 
 * @param errors - Array of validation errors from validateMandatoryFields
 * @param container - Container element (default: document)
 */
export function highlightValidationErrors(
  errors: FieldValidationError[],
  container: HTMLElement | Document = document
): void {
  // First, clear any existing error highlights
  const existingErrors = container.querySelectorAll('.input-error, .select-error, .textarea-error');
  existingErrors.forEach(el => {
    el.classList.remove('input-error', 'select-error', 'textarea-error');
  });
  
  // Remove existing error messages
  const existingMessages = container.querySelectorAll('.field-error-message');
  existingMessages.forEach(el => el.remove());
  
  // Highlight each error field
  let firstErrorElement: HTMLElement | null = null;
  
  for (const error of errors) {
    const element = container.querySelector(`#${CSS.escape(error.htmlId)}`) as HTMLElement;
    if (!element) continue;
    
    // Add error class based on element type
    if (element instanceof HTMLInputElement) {
      element.classList.add('input-error');
    } else if (element instanceof HTMLSelectElement) {
      element.classList.add('select-error');
    } else if (element instanceof HTMLTextAreaElement) {
      element.classList.add('textarea-error');
    }
    
    // Add error message below the field
    const errorMessage = document.createElement('div');
    errorMessage.className = 'field-error-message text-error text-xs mt-1';
    errorMessage.textContent = error.message;
    
    // Insert after the field or its container
    const fieldContainer = element.closest('.form-control') || element.parentElement;
    if (fieldContainer) {
      fieldContainer.appendChild(errorMessage);
    }
    
    // Track first error for focus
    if (!firstErrorElement) {
      firstErrorElement = element;
    }
  }
  
  // Scroll to and focus first error
  if (firstErrorElement) {
    firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => {
      firstErrorElement?.focus();
    }, 300);
  }
}

/**
 * Clear validation error highlighting from the form.
 * 
 * @param container - Container element (default: document)
 */
export function clearValidationErrors(
  container: HTMLElement | Document = document
): void {
  // Remove error classes
  const errorInputs = container.querySelectorAll('.input-error, .select-error, .textarea-error');
  errorInputs.forEach(el => {
    el.classList.remove('input-error', 'select-error', 'textarea-error');
  });
  
  // Remove error messages
  const errorMessages = container.querySelectorAll('.field-error-message');
  errorMessages.forEach(el => el.remove());
}

/**
 * Log all fields for an entity type - useful for debugging and documentation.
 * 
 * @param entityType - Entity type (e.g., "Contact")
 */
export function logEntityFields(entityType: string): void {
  const fields = getEntityFieldDescriptions(entityType);
  
  console.group(`Fields for ${entityType}`);
  console.table(fields.map(fd => ({
    FieldId: fd.FieldId,
    HtmlId: buildFieldId(fd),
    TableName: fd.TableName,
    TableFieldName: fd.TableFieldName,
    FieldLabel: fd.FieldLabel,
    FieldTypeId: fd.FieldTypeId
  })));
  console.groupEnd();
}

// ============================================================================
// DYNAMIC FIELD RENDERING
// ============================================================================
// Centralized field rendering - forms just call renderField() for each
// FieldDescription without knowing anything about field types.
// ============================================================================

/**
 * Rendered field result containing the DOM element and optional component reference
 */
export interface RenderedField {
  /** The wrapper element containing the field */
  element: HTMLElement;
  /** The HTML ID of the field */
  htmlId: string;
  /** Optional component reference (DatePicker, CurrencyAmountPeriod, CurrencyAmount, FileLink, ContactLink, CompanyLink, etc.) */
  component?: DatePicker | CurrencyAmountPeriod | CurrencyAmount | FileLink | ContactLink | CompanyLink;
  /** The field type name for reference */
  fieldTypeName: string;
}

/**
 * Options for renderField
 */
export interface RenderFieldOptions {
  /** Size variant: 'xs' | 'sm' | 'md' | 'lg' (default: 'sm') */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /** Show field label (default: true) */
  showLabel?: boolean;
  /** Show field type badge (default: false) */
  showTypeBadge?: boolean;
  /** Component registry for tracking created components */
  registry?: ComponentRegistry;
  /** Override the field label from FieldDescription */
  labelOverride?: string;
  /** Callback for Image field upload button click */
  onImageUpload?: (htmlId: string, imgElement: HTMLImageElement) => void;
  /** Callback for Image field clear button click */
  onImageClear?: (htmlId: string, imgElement: HTMLImageElement) => void;
  /** Hide the search button (for Contact/File Reference fields) */
  hideSearchButton?: boolean;
  /** Hide the open button (for Contact/File Reference fields) */
  hideOpenButton?: boolean;
  /** Hide the clear button (for Contact/File Reference fields) */
  hideClearButton?: boolean;
  /** Custom options for SkillCombo/Lookup fields (overrides Standing Data lookup) */
  customOptions?: Array<{ value: string | number; label: string }>;
  /** Show time selection (hour 0-23, minute 0-59) for Date fields (default: false) */
  showTime?: boolean;
  /** Show day of week name (e.g., "Wed 28 Jan 2026") for Date fields (default: false) */
  showDay?: boolean;
  /** Minute interval for time selection (default: 1). Use 5 for 5-minute intervals (00, 05, 10, ..., 55) */
  minuteInterval?: number;
}

/**
 * Get lookup options for a field from Standing Data.
 * 
 * Priority:
 * 1. SkillComboCategory - if set, find Skills by Category name
 * 2. Special FieldIds - e.g. FieldId 25 (IndustryId) uses Industries collection
 * 3. FieldId - find Skills by FieldId property
 * 
 * @param fd - Field description to get options for
 * @returns Array of value/label options for dropdown
 */
/**
 * Sort comparator for Skills - sorts by Ordering (lower first), 
 * then alphabetically by Skill name. Items without Ordering go to end.
 */
function sortByOrdering(a: any, b: any): number {
  const aOrder = a.Ordering ?? Number.MAX_SAFE_INTEGER;
  const bOrder = b.Ordering ?? Number.MAX_SAFE_INTEGER;
  if (aOrder !== bOrder) return aOrder - bOrder;
  // Same ordering - sort alphabetically by name
  return (a.Skill || a.Name || '').localeCompare(b.Skill || b.Name || '');
}

/**
 * Build HTML for select options with DefaultValue support.
 * 
 * If the field has a DefaultValue, that option is pre-selected and "-- Select --" is not shown.
 * Otherwise, "-- Select --" is shown as the first (selected) option.
 * 
 * @param options - Array of value/label pairs for dropdown options
 * @param defaultValue - DefaultValue from FieldDescription (or undefined)
 * @returns HTML string for select options
 */
function buildSelectOptionsHtml(
  options: Array<{ value: string; label: string }>,
  defaultValue?: string | null
): string {
  // If there's a default value that exists in the options, pre-select it
  const hasValidDefault = defaultValue && options.some(o => o.value === defaultValue);
  
  // Build options HTML with selected attribute on the default
  const optionsHtml = options
    .map(o => {
      const selected = hasValidDefault && o.value === defaultValue ? ' selected' : '';
      return `<option value="${o.value}"${selected}>${o.label}</option>`;
    })
    .join('');
  
  // Only show "-- Select --" if there's no valid default value
  if (hasValidDefault) {
    return optionsHtml;
  }
  
  return '<option value="">-- Select --</option>' + optionsHtml;
}

function getFieldLookupOptions(fd: FieldDescription): Array<{ value: string; label: string }> {
  const standingData = trisysAPI.getCachedStandingData();
  if (!standingData) return [];
  
  // 1. ContactTitle field → ContactTitles collection
  if (fd.TableFieldName === 'ContactTitle' && standingData.ContactTitles) {
    const titles = [...standingData.ContactTitles].sort(sortByOrdering);
    return titles.map((t: any) => ({
      value: t.Name,
      label: t.Name
    }));
  }
  
  // 2. Type field → EntityTypes filtered by TableName
  if (fd.TableFieldName === 'Type' && standingData.EntityTypes) {
    const entityTypes = (standingData.EntityTypes as any[])
      .filter((et: any) => et.EntityName === fd.TableName)
      .sort((a, b) => (a.Name || '').localeCompare(b.Name || ''));
    
    if (entityTypes.length > 0) {
      return entityTypes.map((et: any) => ({
        value: et.Name,
        label: et.Name
      }));
    }
  }
  
  // 3. Priority field → ContactPriorities collection (special field)
  if (fd.TableFieldName === 'Priority' && fd.TableName === 'Contact' && standingData.ContactPriorities) {
    const priorities = [...standingData.ContactPriorities].sort(sortByOrdering);
    return priorities.map((p: any) => ({
      value: p.ContactPriority,
      label: p.ContactPriority
    }));
  }
  
  // 4. Check for SkillComboCategory - find Skills by Category name
  if (fd.SkillComboCategory) {
    const skills = (standingData.Skills || [])
      .filter((s: any) => s.Category === fd.SkillComboCategory)
      .sort(sortByOrdering);
    
    if (skills.length > 0) {
      return skills.map((s: any) => ({
        value: s.Skill,
        label: s.Skill
      }));
    }
  }
  
  // 5. Special FieldIds - handle collections like Industries
  if (fd.FieldId === 25 && standingData.Industries) {
    // IndustryId field - use Industries collection
    const industries = (standingData.Industries as any[])
      .sort(sortByOrdering);
    
    return industries.map((i: any) => ({
      value: i.Name,
      label: i.Name
    }));
  }
  
  // 6. Default - find Skills by FieldId
  if (!standingData.Skills) return [];
  
  const skills = standingData.Skills
    .filter((s: any) => s.FieldId === fd.FieldId)
    .sort(sortByOrdering);
  
  return skills.map((s: any) => ({
    value: s.Skill,
    label: s.Skill
  }));
}

/**
 * Render a form field based on its FieldDescription.
 * 
 * This is the centralized field factory - forms iterate FieldDescriptions and call
 * this function without knowing anything about how each field type renders.
 * 
 * @param fd - Field description from Standing Data
 * @param options - Render options
 * @returns RenderedField with element and optional component
 */
export async function renderField(
  fd: FieldDescription,
  options: RenderFieldOptions = {}
): Promise<RenderedField> {
  const {
    size = 'sm',
    showLabel = true,
    showTypeBadge = false,
    registry,
    labelOverride,
    onImageUpload,
    onImageClear,
    hideSearchButton = false,
    hideOpenButton = false,
    hideClearButton = false
  } = options;
  
  const htmlId = buildFieldId(fd);
  const sizeClass = `input-${size}`;
  const selectSizeClass = `select-${size}`;
  const textareaSizeClass = `textarea-${size}`;
  
  const wrapper = document.createElement('div');
  wrapper.className = 'form-control';
  
  // Legacy field type overrides - convert specific fields to use correct FieldTypeName
  // These fields have incorrect FieldTypeId in the database and need on-the-fly correction
  // Keys use the full htmlId format: {TableName}_{TableFieldName}
  const legacyFieldTypeOverrides: Record<string, string> = {
    'Contact_EMail': 'E-Mail',
    'Contact_MobileTelNo': 'Telephone',
    'Contact_HomeAddressTelNo': 'Telephone',
    'Contact_WorkTelNo': 'Telephone'
  };
  
  const overriddenTypeName = legacyFieldTypeOverrides[htmlId];
  if (overriddenTypeName) {
    fd = { ...fd, FieldTypeName: overriddenTypeName };
  }
  
  // Label - use override if provided, otherwise strip & from FieldLabel (legacy Windows accelerator key prefix)
  const displayLabel = labelOverride || (fd.FieldLabel || fd.TableFieldName).replace(/&/g, '');
  
  if (showLabel) {
    const label = document.createElement('label');
    label.className = 'label py-0.5';
    label.innerHTML = `
      <span class="label-text text-xs font-medium truncate" title="${displayLabel}">${displayLabel}</span>
      ${showTypeBadge ? `<span class="label-text-alt text-xs opacity-50 badge badge-ghost badge-xs" title="Type: ${fd.FieldTypeName}">${fd.FieldTypeName}</span>` : ''}
    `;
    wrapper.appendChild(label);
  }
  
  let component: DatePicker | CurrencyAmountPeriod | CurrencyAmount | FileLink | ContactLink | CompanyLink | undefined;
  
  // Handle Text fields with Lookup: true as select dropdowns
  // These have FieldTypeName "Text" but should render as <select> with lookup options
  if (fd.FieldTypeName === 'Text' && fd.Lookup) {
    const input = document.createElement('select');
    input.id = htmlId;
    input.className = `select select-bordered ${selectSizeClass} w-full`;
    
    const lookupOptions = getFieldLookupOptions(fd);
    input.innerHTML = buildSelectOptionsHtml(lookupOptions, fd.DefaultValue);
    
    wrapper.appendChild(input);
    
    return {
      element: wrapper,
      htmlId,
      component,
      fieldTypeName: fd.FieldTypeName
    };
  }
  
  // Create input based on field type
  switch (fd.FieldTypeName) {
    case 'Date': {
      const container = document.createElement('div');
      container.id = `${htmlId}-container`;
      container.className = 'w-full';
      
      // Check if time selection is enabled
      const showTime = options.showTime === true;
      // Check if day name should be shown (e.g., "Wed 28 Jan 2026")
      const showDay = options.showDay === true;
      const dateFormat = showDay ? 'DDD DD MMM YYYY' as const : 'DD MMM YYYY' as const;
      
      if (showTime) {
        // Create flex container for date picker + time dropdowns
        container.className = 'w-full flex flex-wrap items-end gap-2';
        
        // Date picker container (takes available space)
        const datePickerContainer = document.createElement('div');
        datePickerContainer.id = `${htmlId}-date-container`;
        datePickerContainer.className = 'flex-1 min-w-32';
        container.appendChild(datePickerContainer);
        
        // Create DatePicker component
        const picker = new DatePicker(datePickerContainer, {
          name: htmlId,
          placeholder: 'Select date...',
          size: size === 'xs' ? 'sm' : size,
          showTodayButton: true,
          format: dateFormat
        });
        await picker.initialize();
        component = picker;
        
        if (registry) {
          registry.datePickers.set(htmlId, picker);
        }
        
        // Time selection container
        const timeContainer = document.createElement('div');
        timeContainer.className = 'flex items-center gap-1';
        
        // Hours dropdown (0-23)
        const hoursSelect = document.createElement('select');
        hoursSelect.id = `${htmlId}-hours`;
        hoursSelect.className = `select select-bordered ${selectSizeClass} w-16`;
        hoursSelect.innerHTML = Array.from({ length: 24 }, (_, h) => 
          `<option value="${h}">${h.toString().padStart(2, '0')}</option>`
        ).join('');
        hoursSelect.title = 'Hour (0-23)';
        
        // Colon separator
        const colonSpan = document.createElement('span');
        colonSpan.className = 'font-bold text-base-content';
        colonSpan.textContent = ':';
        
        // Minutes dropdown - supports interval (e.g., 5 for 00, 05, 10, ..., 55)
        const interval = options.minuteInterval && options.minuteInterval > 0 ? options.minuteInterval : 1;
        const minuteValues = Array.from({ length: Math.floor(60 / interval) }, (_, i) => i * interval);
        const minutesSelect = document.createElement('select');
        minutesSelect.id = `${htmlId}-minutes`;
        minutesSelect.className = `select select-bordered ${selectSizeClass} w-16`;
        minutesSelect.innerHTML = minuteValues.map(m => 
          `<option value="${m}">${m.toString().padStart(2, '0')}</option>`
        ).join('');
        minutesSelect.title = interval > 1 ? `Minute (${interval}-minute intervals)` : 'Minute (0-59)';
        
        timeContainer.appendChild(hoursSelect);
        timeContainer.appendChild(colonSpan);
        timeContainer.appendChild(minutesSelect);
        container.appendChild(timeContainer);
      } else {
        // Standard date-only picker
        // Create DatePicker component
        const picker = new DatePicker(container, {
          name: htmlId,
          placeholder: 'Select date...',
          size: size === 'xs' ? 'sm' : size,
          showTodayButton: true,
          format: dateFormat
        });
        await picker.initialize();
        component = picker;
        
        if (registry) {
          registry.datePickers.set(htmlId, picker);
        }
      }
      
      wrapper.appendChild(container);
      break;
    }
    
    case 'CurrencyAmountPeriod': {
      const container = document.createElement('div');
      container.id = `${htmlId}-container`;
      container.className = 'w-full';
      wrapper.appendChild(container);
      
      // Get standing data for currencies and periods
      const standingData = trisysAPI.getCachedStandingData();
      if (standingData) {
        const currencies = (standingData.Currencies || []).map(c => ({
          CurrencyId: c.CurrencyId,
          Symbol: c.Symbol,
          Description: c.Description,
          Ordering: c.Ordering
        }));
        
        const periods = (standingData.ConsultancyPeriods || []).map(p => ({
          ConsultancyPeriodId: p.ConsultancyPeriodId,
          Name: p.Name
        }));
        
        const cap = new CurrencyAmountPeriod(container, {
          name: htmlId,
          currencies,
          periods,
          size: size === 'xs' ? 'sm' : size
        });
        await cap.initialize();
        component = cap;
        
        if (registry) {
          registry.currencyAmountPeriods.set(htmlId, cap);
        }
      }
      break;
    }
    
    case 'Yes/No': {
      // Special case: ContactMarried should be a dropdown with Single/Married
      // (value 0=Single, 1=Married as per Apex SDK pattern)
      if (fd.TableFieldName === 'ContactMarried') {
        const select = document.createElement('select');
        select.id = htmlId;
        select.className = `select select-bordered ${selectSizeClass} w-full`;
        select.innerHTML = `
          <option value="">-- Select --</option>
          <option value="0">Single</option>
          <option value="1">Married</option>
        `;
        wrapper.appendChild(select);
      } else {
        // Standard Yes/No checkbox
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.id = htmlId;
        input.className = `checkbox checkbox-${size} checkbox-primary`;
        
        // Wrap in a container for consistent layout
        const checkboxWrapper = document.createElement('div');
        checkboxWrapper.className = 'flex items-center h-8';
        checkboxWrapper.appendChild(input);
        wrapper.appendChild(checkboxWrapper);
      }
      break;
    }
    
    case 'Lookup':
    case 'LookupMulti': {
      const input = document.createElement('select');
      input.id = htmlId;
      input.className = `select select-bordered ${selectSizeClass} w-full`;
      
      // Populate options - checks SkillComboCategory, special FieldIds, then Skills by FieldId
      const lookupOpts = getFieldLookupOptions(fd);
      input.innerHTML = buildSelectOptionsHtml(lookupOpts, fd.DefaultValue);
      
      wrapper.appendChild(input);
      break;
    }
    
    case 'User': {
      // User field - dropdown populated from Users in standing data
      const input = document.createElement('select');
      input.id = htmlId;
      input.className = `select select-bordered ${selectSizeClass} w-full`;
      
      const standingData = trisysAPI.getCachedStandingData();
      const users = (standingData?.Users || [])
        .filter((u: any) => !u.Locked)
        .sort((a: any, b: any) => (a.FullName || '').localeCompare(b.FullName || ''));
      
      input.innerHTML = '<option value="">-- Select --</option>' + 
        users.map((u: any) => `<option value="${u.UserId}">${u.FullName}</option>`).join('');
      
      wrapper.appendChild(input);
      break;
    }
    
    case 'SkillCombo': {
      // SkillCombo field - dropdown populated from:
      // 1. customOptions if provided in RenderFieldOptions
      // 2. SkillComboCategory if set on FieldDescription
      // 3. Skills by FieldId from Standing Data
      const input = document.createElement('select');
      input.id = htmlId;
      input.className = `select select-bordered ${selectSizeClass} w-full`;
      
      // Check for custom options first (programmatic list)
      if (options.customOptions && options.customOptions.length > 0) {
        const customOpts = options.customOptions.map(o => ({
          value: String(o.value),
          label: o.label
        }));
        input.innerHTML = buildSelectOptionsHtml(customOpts, fd.DefaultValue);
      } else {
        // getFieldLookupOptions handles SkillComboCategory, special FieldIds, and Skills by FieldId
        const skillOpts = getFieldLookupOptions(fd);
        input.innerHTML = buildSelectOptionsHtml(skillOpts, fd.DefaultValue);
      }
      
      wrapper.appendChild(input);
      break;
    }
    
    case 'Memo': {
      const input = document.createElement('textarea');
      input.id = htmlId;
      input.className = `textarea textarea-bordered ${textareaSizeClass} w-full`;
      input.rows = 2;
      wrapper.appendChild(input);
      break;
    }
    
    case 'Integer': {
      const input = document.createElement('input');
      input.id = htmlId;
      input.type = 'number';
      input.step = '1';
      input.pattern = '[0-9]*';
      input.inputMode = 'numeric';
      input.className = `input input-bordered ${sizeClass} w-full`;
      
      // Prevent decimal point and non-integer input
      input.addEventListener('keydown', (e) => {
        // Allow: backspace, delete, tab, escape, enter, arrows
        if ([8, 9, 27, 13, 46, 37, 38, 39, 40].includes(e.keyCode)) return;
        // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        if ((e.ctrlKey || e.metaKey) && [65, 67, 86, 88].includes(e.keyCode)) return;
        // Block decimal point
        if (e.key === '.' || e.key === ',') {
          e.preventDefault();
          return;
        }
        // Allow numbers only
        if (!/^[0-9-]$/.test(e.key)) {
          e.preventDefault();
        }
      });
      
      // Clean up pasted content to remove decimals
      input.addEventListener('input', () => {
        const value = input.value;
        if (value.includes('.') || value.includes(',')) {
          input.value = value.replace(/[.,].*/g, '');
        }
      });
      
      wrapper.appendChild(input);
      break;
    }
    
    case 'Float':
    case 'Percent':
    case 'Currency': {
      const input = document.createElement('input');
      input.id = htmlId;
      input.type = 'number';
      input.step = '0.01';
      input.className = `input input-bordered ${sizeClass} w-full`;
      wrapper.appendChild(input);
      break;
    }
    
    case 'CurrencyAmount': {
      // CurrencyAmount - currency dropdown + amount input field
      const container = document.createElement('div');
      container.id = `${htmlId}-container`;
      container.className = 'w-full';
      wrapper.appendChild(container);
      
      // Get standing data for currencies
      const standingData = trisysAPI.getCachedStandingData();
      if (standingData) {
        const currencies = (standingData.Currencies || []).map((c: any) => ({
          CurrencyId: c.CurrencyId,
          Symbol: c.Symbol,
          Description: c.Description,
          Ordering: c.Ordering
        }));
        
        const ca = new CurrencyAmount(container, {
          name: htmlId,
          currencies,
          size: size === 'xs' ? 'sm' : size
        });
        await ca.initialize();
        component = ca;
        
        if (registry) {
          registry.currencyAmounts.set(htmlId, ca);
        }
      }
      break;
    }
    
    case 'File Reference': {
      // File reference field - use FileLink component with find/open/clear buttons
      // This matches the Link 1-6 pattern in contactDetailV2 identification fields
      const container = document.createElement('div');
      container.id = `${htmlId}-container`;
      container.className = 'w-full';
      wrapper.appendChild(container);
      
      // Create FileLink component
      const fileLink = new FileLink({
        id: htmlId,
        label: displayLabel,
        tableName: fd.TableName,
        tableFieldName: fd.TableFieldName,
        color: 'primary',
        size: size === 'xs' ? 'sm' : size,
        showLabel: false  // Label already added by renderField wrapper
      });
      await fileLink.initialize();
      fileLink.renderTo(container);
      component = fileLink;
      
      if (registry) {
        registry.fileLinks.set(htmlId, fileLink);
      }
      break;
    }
    
    case 'Contact': {
      // Contact reference field - use ContactLink component with search/open/clear buttons
      // This matches the FileLink pattern but for Contact lookups (FieldTypeId: 19)
      const container = document.createElement('div');
      container.id = `${htmlId}-container`;
      container.className = 'w-full';
      wrapper.appendChild(container);
      
      // Create ContactLink component
      const contactLink = new ContactLink({
        id: htmlId,
        label: displayLabel,
        color: 'primary',
        size: size === 'xs' ? 'sm' : size,
        showLabel: false,  // Label already added by renderField wrapper
        apiService: trisysApi,  // Pass API service for ContactPicker
        hideSearchButton,
        hideOpenButton,
        hideClearButton
      });
      await contactLink.initialize();
      contactLink.renderTo(container);
      component = contactLink;
      
      // Attach component reference to DOM element for auto-discovery
      const inputEl = container.querySelector(`#${htmlId}`) as HTMLElement;
      if (inputEl) {
        (inputEl as any).__component = contactLink;
      }
      
      if (registry) {
        registry.contactLinks.set(htmlId, contactLink);
      }
      break;
    }
    
    case 'Company': {
      // Company reference field - use CompanyLink component with search/open/clear buttons
      // This matches the ContactLink pattern but for Company lookups (FieldTypeId: 20)
      const container = document.createElement('div');
      container.id = `${htmlId}-container`;
      container.className = 'w-full';
      wrapper.appendChild(container);
      
      // Create CompanyLink component
      const companyLink = new CompanyLink({
        id: htmlId,
        label: displayLabel,
        color: 'primary',
        size: size === 'xs' ? 'sm' : size,
        showLabel: false,  // Label already added by renderField wrapper
        apiService: trisysApi,  // Pass API service for CompanyPicker
        hideSearchButton,
        hideOpenButton,
        hideClearButton
      });
      await companyLink.initialize();
      companyLink.renderTo(container);
      component = companyLink;
      
      // Attach component reference to DOM element for auto-discovery
      const inputEl = container.querySelector(`#${htmlId}`) as HTMLElement;
      if (inputEl) {
        (inputEl as any).__component = companyLink;
      }
      
      if (registry) {
        registry.companyLinks.set(htmlId, companyLink);
      }
      break;
    }
    
    case 'Text': {
      // Check if this Text field has lookup options
      // Uses SkillComboCategory, special FieldIds (like Industries), or Skills by FieldId
      const lookupOptions = getFieldLookupOptions(fd);
      
      if (lookupOptions.length > 0) {
        // Render as dropdown with options
        const input = document.createElement('select');
        input.id = htmlId;
        input.className = `select select-bordered ${selectSizeClass} w-full`;
        input.innerHTML = '<option value="">-- Select --</option>' + 
          lookupOptions.map(o => `<option value="${o.value}">${o.label}</option>`).join('');
        wrapper.appendChild(input);
      } else {
        // Regular text field - use textarea if MultiLine, FieldLength is 0 (unlimited) or > 255
        const useTextarea = fd.MultiLine || fd.FieldLength === 0 || fd.FieldLength > 255;
        if (useTextarea) {
          const input = document.createElement('textarea');
          input.id = htmlId;
          input.className = `textarea textarea-bordered ${textareaSizeClass} w-full`;
          input.rows = 3;
          wrapper.appendChild(input);
        } else {
          const input = document.createElement('input');
          input.id = htmlId;
          input.type = 'text';
          if (fd.FieldLength > 0) {
            input.maxLength = fd.FieldLength;
          }
          input.className = `input input-bordered ${sizeClass} w-full`;
          wrapper.appendChild(input);
        }
      }
      break;
    }
    
    case 'Image': {
      // Image field - shows thumbnail with upload/clear buttons
      const imageWrapper = document.createElement('div');
      imageWrapper.className = 'flex gap-2 items-start';
      
      // Thumbnail container - clickable to view full image
      const thumbContainer = document.createElement('div');
      thumbContainer.className = 'w-16 h-16 border border-base-300 rounded-lg overflow-hidden bg-base-200 flex items-center justify-center cursor-pointer hover:ring-2 hover:ring-primary';
      thumbContainer.title = 'Click to view full image';
      
      const img = document.createElement('img');
      img.id = htmlId;
      img.className = 'w-full h-full object-cover hidden';
      img.alt = fd.FieldLabel || fd.TableFieldName;
      
      const placeholder = document.createElement('i');
      placeholder.id = `${htmlId}-placeholder`;
      placeholder.className = 'fas fa-image text-base-content/30 text-xl';
      
      thumbContainer.appendChild(img);
      thumbContainer.appendChild(placeholder);
      
      // Click on thumbnail to show full image in ImageViewer
      thumbContainer.addEventListener('click', () => {
        if (img.src && !img.classList.contains('hidden')) {
          // Show image using the standard ImageViewer component
          import('@/components/ImageViewer').then(({ ImageViewer }) => {
            ImageViewer.show({
              imageUrl: img.src,
              alt: img.alt,
              title: fd.FieldLabel || 'Image',
              initialSize: 'medium'
            });
          });
        }
      });
      
      // Show image and hide placeholder when src is set
      img.onload = () => {
        img.classList.remove('hidden');
        placeholder.classList.add('hidden');
      };
      img.onerror = () => {
        img.classList.add('hidden');
        placeholder.classList.remove('hidden');
      };
      
      // Buttons container
      const btnContainer = document.createElement('div');
      btnContainer.className = 'flex flex-col gap-1';
      
      const uploadBtn = document.createElement('button');
      uploadBtn.type = 'button';
      uploadBtn.id = `${htmlId}-upload-btn`;
      uploadBtn.className = `btn btn-ghost btn-${size}`;
      uploadBtn.innerHTML = '<i class="fas fa-upload"></i>';
      uploadBtn.title = 'Upload image';
      
      // Attach upload handler if provided
      if (onImageUpload) {
        uploadBtn.addEventListener('click', () => onImageUpload(htmlId, img));
      }
      
      const clearBtn = document.createElement('button');
      clearBtn.type = 'button';
      clearBtn.id = `${htmlId}-clear-btn`;
      clearBtn.className = `btn btn-ghost btn-${size} text-error`;
      clearBtn.innerHTML = '<i class="fas fa-times"></i>';
      clearBtn.title = 'Clear image';
      
      // Attach clear handler if provided
      if (onImageClear) {
        clearBtn.addEventListener('click', () => onImageClear(htmlId, img));
      }
      
      btnContainer.appendChild(uploadBtn);
      btnContainer.appendChild(clearBtn);
      
      imageWrapper.appendChild(thumbContainer);
      imageWrapper.appendChild(btnContainer);
      wrapper.appendChild(imageWrapper);
      break;
    }
    
    case 'Link': {
      // Read-only link field
      const input = document.createElement('input');
      input.id = htmlId;
      input.type = 'text';
      input.readOnly = true;
      input.className = `input input-bordered ${sizeClass} w-full bg-base-200 cursor-not-allowed`;
      wrapper.appendChild(input);
      break;
    }
    
    case 'Internet': {
      // Internet/URL field - text input with globe button to open in new tab
      const inputGroup = document.createElement('div');
      inputGroup.className = 'join w-full';
      
      const input = document.createElement('input');
      input.id = htmlId;
      input.type = 'url';
      input.placeholder = 'https://';
      input.className = `input input-bordered ${sizeClass} join-item flex-1`;
      
      const openBtn = document.createElement('button');
      openBtn.type = 'button';
      openBtn.id = `${htmlId}-open-btn`;
      openBtn.className = `btn btn-${size} join-item bg-base-100 hover:bg-base-200 border border-base-content/20`;
      openBtn.innerHTML = '<i class="fas fa-globe" style="color: #3b82f6;"></i>';
      openBtn.title = 'Open in new tab';
      openBtn.addEventListener('click', () => {
        const url = input.value?.trim();
        if (url) {
          // Ensure URL has a protocol
          const fullUrl = url.startsWith('http://') || url.startsWith('https://') 
            ? url 
            : `https://${url}`;
          window.open(fullUrl, '_blank', 'noopener,noreferrer');
        }
      });
      
      inputGroup.appendChild(input);
      inputGroup.appendChild(openBtn);
      wrapper.appendChild(inputGroup);
      break;
    }
    
    case 'E-Mail': {
      // E-Mail field - text input with email button to open mailto:
      const inputGroup = document.createElement('div');
      inputGroup.className = 'join w-full';
      
      const input = document.createElement('input');
      input.id = htmlId;
      input.type = 'email';
      input.placeholder = 'email@example.com';
      input.pattern = '[a-zA-Z0-9._%+\\-]+@[a-zA-Z0-9.\\-]+\\.[a-zA-Z]{2,}';
      input.className = `input input-bordered ${sizeClass} join-item flex-1`;
      
      // Only allow valid email characters: letters, numbers, @, ., -, _, +, %
      input.addEventListener('keydown', (e) => {
        // Allow: backspace, delete, tab, escape, enter, arrows
        if ([8, 9, 27, 13, 46, 37, 38, 39, 40].includes(e.keyCode)) return;
        // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        if ((e.ctrlKey || e.metaKey) && [65, 67, 86, 88].includes(e.keyCode)) return;
        // Allow valid email characters only
        if (!/^[a-zA-Z0-9@.\-_+%]$/.test(e.key)) {
          e.preventDefault();
        }
      });
      
      // Clean pasted content to remove invalid characters
      input.addEventListener('input', () => {
        const value = input.value;
        const cleaned = value.replace(/[^a-zA-Z0-9@.\-_+%]/g, '');
        if (value !== cleaned) {
          input.value = cleaned;
        }
      });
      
      const openBtn = document.createElement('button');
      openBtn.type = 'button';
      openBtn.id = `${htmlId}-open-btn`;
      openBtn.className = `btn btn-${size} join-item bg-base-100 hover:bg-base-200 border border-base-content/20`;
      openBtn.innerHTML = '<i class="fas fa-envelope" style="color: #3b82f6;"></i>';
      openBtn.title = 'Send email';
      openBtn.addEventListener('click', () => {
        const email = input.value?.trim();
        if (email) {
          window.location.href = `mailto:${email}`;
        }
      });
      
      inputGroup.appendChild(input);
      inputGroup.appendChild(openBtn);
      wrapper.appendChild(inputGroup);
      break;
    }
    
    case 'Telephone': {
      // Telephone field - text input with phone button to initiate call
      const inputGroup = document.createElement('div');
      inputGroup.className = 'join w-full';
      
      const input = document.createElement('input');
      input.id = htmlId;
      input.type = 'tel';
      input.placeholder = '+44 1234 567890';
      input.className = `input input-bordered ${sizeClass} join-item flex-1`;
      
      const openBtn = document.createElement('button');
      openBtn.type = 'button';
      openBtn.id = `${htmlId}-open-btn`;
      openBtn.className = `btn btn-${size} join-item bg-base-100 hover:bg-base-200 border border-base-content/20`;
      openBtn.innerHTML = '<i class="fas fa-phone" style="color: #3b82f6;"></i>';
      openBtn.title = 'Call number';
      openBtn.addEventListener('click', () => {
        const phone = input.value?.trim();
        if (phone) {
          TelephoneService.call(phone);
        }
      });
      
      inputGroup.appendChild(input);
      inputGroup.appendChild(openBtn);
      wrapper.appendChild(inputGroup);
      break;
    }
    
    case 'Panel':
    case 'TreeView':
    case 'Group': {
      // Non-editable structural fields - just show a placeholder
      const placeholder = document.createElement('div');
      placeholder.id = htmlId;
      placeholder.className = 'text-xs text-base-content/40 italic px-2 py-1';
      placeholder.textContent = `[${fd.FieldTypeName}]`;
      wrapper.appendChild(placeholder);
      break;
    }
    
    default: {
      // Default to text input for Text, Email, Phone, URL, etc.
      const input = document.createElement('input');
      input.id = htmlId;
      input.type = 'text';
      input.className = `input input-bordered ${sizeClass} w-full`;
      wrapper.appendChild(input);
    }
  }
  
  return {
    element: wrapper,
    htmlId,
    component,
    fieldTypeName: fd.FieldTypeName
  };
}

/**
 * Render all fields for an entity type.
 * 
 * @param entityType - Entity type (e.g., "Contact")
 * @param options - Render options
 * @returns Array of RenderedField results
 */
export async function renderEntityFields(
  entityType: string,
  options: RenderFieldOptions = {}
): Promise<RenderedField[]> {
  const fieldDescriptions = getEntityFieldDescriptions(entityType);
  const results: RenderedField[] = [];
  
  for (const fd of fieldDescriptions) {
    const rendered = await renderField(fd, options);
    results.push(rendered);
  }
  
  return results;
}

/**
 * Result of rendering trisys-field elements
 */
export interface TrisysFieldsResult {
  /** Number of <trisys-field> elements found */
  found: number;
  /** Successfully rendered field IDs */
  rendered: string[];
  /** Field IDs with no matching FieldDescription */
  notFound: string[];
  /** Field IDs that failed to render */
  errors: string[];
}

/**
 * Build a virtual FieldDescription for custom/modal fields.
 * This allows <trisys-field> to be used in modals and other non-entity forms
 * while maintaining conceptual integrity and consistent look/feel.
 * 
 * @param fieldId - The field ID (used as both id and TableFieldName)
 * @param typeName - The FieldTypeName (e.g., "File Reference", "Date", "Text", "Lookup")
 * @param options - Additional options to customize the field
 */
function buildVirtualFieldDescription(
  fieldId: string,
  typeName: string,
  options: {
    label?: string;
    multiLine?: boolean;
    mandatory?: boolean;
    fieldLength?: number;
    skillCategory?: string;
    lookup?: boolean;
    defaultValue?: string;
  } = {}
): FieldDescription {
  // Map common type names to FieldTypeIds (for compatibility)
  const typeIdMap: Record<string, number> = {
    'Text': 1,
    'Date': 6,
    'Yes/No': 4,
    'Lookup': 3,
    'LookupMulti': 3,
    'User': 11,
    'CurrencyAmountPeriod': 18,
    'CurrencyAmount': 19,
    'File Reference': 14,
    'E-Mail': 15,
    'Internet': 16,
    'Telephone': 17,
    'Memo': 2,
    'Contact': 19
  };
  
  return {
    FieldId: 0, // Virtual field - no real FieldId
    TableFieldName: fieldId.includes('_') ? fieldId.split('_')[1] : fieldId,
    TableName: fieldId.includes('_') ? fieldId.split('_')[0] : 'Virtual',
    FieldTypeId: typeIdMap[typeName] || 1,
    FieldTypeName: typeName,
    Description: null,
    Fixed: false,
    FieldLength: options.fieldLength || 255,
    FieldLabel: options.label || fieldId.replace(/[-_]/g, ' ').replace(/([A-Z])/g, ' $1').trim(),
    Lookup: options.lookup || false,
    AddToLookups: false,
    OvertypeLookups: false,
    MultiLine: options.multiLine || false,
    MinValue: null,
    MaxValue: null,
    DefaultValue: options.defaultValue || null,
    Mandatory: options.mandatory || false,
    DisplayFormat: null,
    SpinIncrement: 0,
    SortAlphabetically: true,
    SearchFieldName: null,
    SkillComboCategory: options.skillCategory || null,
    Locked: false,
    RequirementCandidateSearchVisible: false,
    Value: null,
    DisplayValue: null
  };
}

/**
 * Scan for <trisys-field> placeholder elements and render them as actual form fields.
 * 
 * This allows HTML templates to define PLACEMENT while centralizing field RENDERING.
 * 
 * Usage in HTML:
 *   <trisys-field id="Contact_Surname"></trisys-field>
 *   <trisys-field id="Contact_DateOfBirth" show-label="false"></trisys-field>
 * 
 * Attributes:
 *   - id (required): The field ID in format TableName_FieldName (e.g., Contact_Surname)
 *   - show-label: "true" (default) or "false" - whether to render the label
 *   - size: "xs", "sm" (default), "md", "lg" - field size
 * 
 * After calling this, the <trisys-field> elements are replaced with rendered form controls
 * that have the correct IDs for autoPopulateFormFields() to populate.
 * 
 * @param container - Container to search within (defaults to document)
 * @param options - Render options (registry for component cleanup)
 * @returns TrisysFieldsResult with counts and lists of processed fields
 */
export async function renderTrisysFields(
  container: HTMLElement | Document = document,
  options: {
    registry?: ComponentRegistry;
    size?: 'xs' | 'sm' | 'md' | 'lg';
  } = {}
): Promise<TrisysFieldsResult> {
  const result: TrisysFieldsResult = {
    found: 0,
    rendered: [],
    notFound: [],
    errors: []
  };
  
  const standingData = trisysAPI.getCachedStandingData();
  if (!standingData?.FieldDescriptions) {
    logger.warn('No FieldDescriptions in cached Standing Data - cannot render trisys-field elements');
    return result;
  }
  
  // Build FieldDescription lookup by TableName_FieldName
  const fdByKey = new Map<string, FieldDescription>();
  for (const fd of standingData.FieldDescriptions) {
    const key = buildFieldId(fd);
    fdByKey.set(key, fd);
  }
  
  // Find all <trisys-field> elements
  const trisysFields = container.querySelectorAll('trisys-field');
  result.found = trisysFields.length;
  
  logger.debug('Found trisys-field elements', { count: result.found });
  
  for (const placeholder of trisysFields) {
    // Support multiple methods of specifying fields:
    // 1. id="TableName_FieldName" - bound to FieldDescriptions (entity forms)
    // 2. data-table="TableName" data-field="FieldName" - bound to FieldDescriptions
    // 3. id="custom-id" data-type="FieldTypeName" - virtual field for modals (no FieldDescription)
    let fieldId = placeholder.id;
    
    const dataTable = placeholder.getAttribute('data-table');
    const dataField = placeholder.getAttribute('data-field');
    const dataType = placeholder.getAttribute('data-type'); // Virtual field type (e.g., "File Reference", "Date")
    
    if (dataTable && dataField) {
      // Build fieldId from data attributes (preferred method for entity-bound fields)
      fieldId = `${dataTable}_${dataField}`;
    }
    
    if (!fieldId) {
      logger.warn('trisys-field element missing id or data-table/data-field attributes', { element: placeholder.outerHTML });
      result.errors.push('(missing id)');
      continue;
    }
    
    // Determine FieldDescription - either from standing data or build a virtual one
    let fd: FieldDescription | undefined;
    
    if (dataType) {
      // Virtual field - build FieldDescription from data-type attribute
      // This allows <trisys-field> to be used in modals with consistent look/feel
      const label = placeholder.getAttribute('label') || undefined;
      const multiLine = placeholder.getAttribute('multiline') === 'true';
      const mandatory = placeholder.getAttribute('mandatory') === 'true';
      const fieldLength = parseInt(placeholder.getAttribute('field-length') || '255', 10);
      const skillCategory = placeholder.getAttribute('skill-category') || undefined;
      const lookup = placeholder.getAttribute('lookup') === 'true';
      const defaultValue = placeholder.getAttribute('default-value') || undefined;
      
      fd = buildVirtualFieldDescription(fieldId, dataType, {
        label,
        multiLine,
        mandatory,
        fieldLength,
        skillCategory,
        lookup,
        defaultValue
      });
      
      logger.debug('Built virtual FieldDescription for trisys-field', { fieldId, dataType, label });
    } else {
      // Look up FieldDescription from standing data
      fd = fdByKey.get(fieldId);
      if (!fd) {
        logger.warn('No FieldDescription found for trisys-field', { fieldId });
        result.notFound.push(fieldId);
        continue;
      }
    }
    
    // Read attributes
    const showLabel = placeholder.getAttribute('show-label') !== 'false';
    const size = (placeholder.getAttribute('size') as 'xs' | 'sm' | 'md' | 'lg') || options.size || 'sm';
    const labelOverride = placeholder.getAttribute('label') || undefined;
    
    // Button visibility options (for Contact/File Reference fields)
    const hideSearchButton = placeholder.getAttribute('hide-search-button') === 'true';
    const hideOpenButton = placeholder.getAttribute('hide-open-button') === 'true';
    const hideClearButton = placeholder.getAttribute('hide-clear-button') === 'true';
    
    // Time selection for Date fields
    const showTime = placeholder.getAttribute('show-time') === 'true';
    // Show day of week name for Date fields (e.g., "Wed 28 Jan 2026")
    const showDay = placeholder.getAttribute('show-day') === 'true';
    // Minute interval for time selection (e.g., 5 for 5-minute intervals: 00, 05, 10, ..., 55)
    const minuteIntervalAttr = placeholder.getAttribute('minute-interval');
    const minuteInterval = minuteIntervalAttr ? parseInt(minuteIntervalAttr, 10) : undefined;
    
    try {
      // Render the field
      const rendered = await renderField(fd, {
        showLabel,
        size,
        registry: options.registry,
        labelOverride,
        hideSearchButton,
        hideOpenButton,
        hideClearButton,
        showTime,
        showDay,
        minuteInterval
      });
      
      // Replace the placeholder with the rendered element
      placeholder.replaceWith(rendered.element);
      
      result.rendered.push(fieldId);
      logger.debug('Rendered trisys-field', { fieldId, fieldType: fd.FieldTypeName });
      
    } catch (err) {
      logger.error('Failed to render trisys-field', { fieldId, error: err });
      result.errors.push(fieldId);
    }
  }
  
  logger.info('Finished rendering trisys-field elements', {
    found: result.found,
    rendered: result.rendered.length,
    notFound: result.notFound.length,
    errors: result.errors.length
  });
  
  return result;
}
